import React, { forwardRef } from 'react';

import styled from 'styled-components';

import { color, variable } from '../../helpers/style.helpers';

import { ListProps } from './List.decl';

import { Box, Flex } from '@aircall/tractor';
import { ThemeProvider } from '@aircall/tractor-v2';
import { ShadowScrollContainer } from '@components/ShadowScrollContainer';
import { Loading } from '@dashboard/library';

// If we only have one container to handle the scroll: the scrollbar is not displayed corectly with the border-radius.
// This wrapper exists and is overflow hidden so that we can hide the border of the scrollbar.
const Container = styled(Box)<ListProps>`
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid ${color.grey.base};
  border-radius: ${variable.radii[8]};
  background: ${color.base.white};
  transition: 500ms box-shadow;
`;

const ItemsContainer = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;
`;

const Item = styled.li`
  border-bottom: 1px solid ${color.grey.base};

  &:last-child {
    border-bottom: none;
  }
`;

const CustomLoaderContainer = styled(Flex)`
  position: absolute;
  z-index: 3;
  bottom: 0;
  width: 100%;
  height: ${variable.space.xl}px;
  padding-left: ${variable.space.s}px;
  background: ${color.base.white};
`;

const OverLoadingContainer = styled(Flex)`
  position: absolute;
  z-index: 10;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  background: rgb(255 255 255 / 0.6);
`;

export const List = forwardRef<HTMLUListElement, ListProps>(
  (
    {
      children,
      forceShadow = false,
      'data-test': dataTest = 'list',
      hasScroll = false,
      isLoadingMoreItems = false,
      customLoader,
      stickyFooter,
      overLoading = false,
      ...otherProps
    },
    ref
  ) => {
    const hasNoChildren =
      React.Children.toArray(children).filter((child) => React.isValidElement(child)).length === 0;
    /* will avoid rendering the 'ul' element with borders
     when the List has null values or invalid values as children
     and stickyFooter is falsy
     */
    if (hasNoChildren && !stickyFooter) {
      return null;
    }

    const items = React.Children.map(
      children,
      (child, index) => child && <Item data-test={`list-item-${index}`}>{child}</Item>
    );

    return (
      <Container {...otherProps}>
        {overLoading && (
          <OverLoadingContainer data-test='list-over-loading'>
            <ThemeProvider>
              <Loading />
            </ThemeProvider>
          </OverLoadingContainer>
        )}
        {hasScroll ? (
          <>
            <ShadowScrollContainer<HTMLUListElement>
              as='ul'
              ref={ref}
              data-test={dataTest}
              forceShadow={forceShadow}
              // should display a border between the last item and stickyFooter
              hasBottomBorder={!!(stickyFooter && !hasNoChildren)}
            >
              {items}
            </ShadowScrollContainer>
            {stickyFooter}
            {isLoadingMoreItems && (
              <CustomLoaderContainer data-test='list-custom-loader'>
                {customLoader}
              </CustomLoaderContainer>
            )}
          </>
        ) : (
          <ItemsContainer data-test={dataTest}>{items}</ItemsContainer>
        )}
      </Container>
    );
  }
);
